import { http } from '@pendo/http';
import { openDB } from 'idb';
import { convertProductLinesArrayToMap, hasPurchasedProductLine } from '@/data/flags/product-lines.js';
import { isExtensionEnabledForSubscription } from '@/vuex/modules/product-lines/product-lines.utils';

/* Dev note: We pass bypassUnauthorizedLogout = true to the http wrapper
 * to avoid a logout/refresh in the event of a 401. These 401s do not reflect
 * the traditional tokens missing/expired 401 response used elsewhere and need
 * to be treated differently. */

export function fetchAll () {
    return http.get('/api/pendozer/subscription').then((res) => res.data);
}

export function fetchAllWithProductLines () {
    return http.get('/api/pendozer/subscription/experiment').then((res) => res.data);
}

export function fetchDeletedSubcriptions () {
    return http.get('/api/pendozer/subscription/experiment?onlyDeleted=true').then((res) => res.data);
}

export function fetchSubscription (subscriptionId) {
    return http
        .get(`/api/super/s/${subscriptionId}`, {
            bypassUnauthorizedLogout: true,
        })
        .then((res) => res.data);
}

export function fetchReadOnlySubscription (subscriptionId) {
    return http.get(`/api/pendozer/s/${subscriptionId}/subscription`).then((res) => res.data);
}

export function fetchApplications (subscriptionId, { includeDeactivated } = {}) {
    const queryParams = includeDeactivated ? '?includeDisabled=true' : '';

    return http
        .get(`/api/pendozer/s/${subscriptionId}/application${queryParams}`, {
            bypassUnauthorizedLogout: true,
        })
        .then((res) => res.data);
}

export async function fetchReadOnlySubscriptionDetails (subscriptionId, { includeDeactivated } = {}) {
    const [subscription, apps, summary] = await Promise.all([
        fetchReadOnlySubscription(subscriptionId),
        fetchApplications(subscriptionId, { includeDeactivated }),
        fetchSummary(subscriptionId),
    ]);

    const { users, permissionGroups } = summary;

    return {
        ...subscription,
        applications: apps,
        users,
        permissionGroups,
    };
}

export function fetchProductLines (subscriptionId) {
    return http
        .get(`/api/super/s/${subscriptionId}/productline`, {
            bypassUnauthorizedLogout: true,
        })
        .then((res) => res.data);
}

export function fetchSummary (subscriptionId) {
    return http
        .get(`/api/pendozer/s/${subscriptionId}/subscription/summary`, {
            bypassUnauthorizedLogout: true,
        })
        .then((res) => res.data);
}

export function fetchFlag ({ subscriptionId, flagName }) {
    return http.get(`/api/super/s/${subscriptionId}/flag/${flagName}`).then((res) => res.data);
}

export function createSubscription (subscription) {
    return http.post('/api/super/subscription/create', subscription).then((res) => res.data);
}

export function requestAutoAccess ({ subscriptionId, username }) {
    return http.post('/api/guestAccess', { subscriptionId, username }).then((res) => res);
}

export function updateAutoGrantAccessExpiresOn ({ id, property, value }) {
    return http.put(`/api/s/${id}/subscription/${property}/${value}`, {}).then((res) => res.data);
}

export function update (subscription) {
    return http.put(`/api/super/s/${subscription.id}`, subscription).then((res) => res.data);
}

export function updateFlag ({ subscriptionId, flag, enabled }) {
    return http.put(`/api/super/s/${subscriptionId}/flag/${flag}`, { set: enabled }).then((res) => res.data);
}

export function setFlagForType ({ type, flag, set, subscriptionId }) {
    return http.put(`/api/super/s/${subscriptionId}/productline/${type}/${flag}`, { set }).then((res) => res.data);
}

export function setProductLineMetadata ({ type, data, subscriptionId }) {
    return http.put(`/api/super/s/${subscriptionId}/productline/${type}/metadata`, { ...data }).then((res) => res.data);
}

export function updatePlan (subscriptionId, body, query = '') {
    return http.put(`/api/super/s/${subscriptionId}/plan${query}`, body).then((res) => res.data);
}

export function updatePackagePricingModel (subscriptionId, pricingModel) {
    return http
        .put(`/api/super/s/${subscriptionId}/subscription/pricingModel`, { pricingModel })
        .then((res) => res.data);
}

export function updateVisitorType (subscriptionId, visitorType) {
    return http.put(`/api/super/s/${subscriptionId}/subscription/visitorType`, { visitorType }).then((res) => res.data);
}

export async function getSuperAiBusinessContext ({ type = 'Subscription', id = '', subId }) {
    return http
        .get(`/api/super/s/${subId}/businessContext/${type}/${id}`)
        .then((response) => response?.data || [])
        .catch((error) => {
            throw error;
        });
}

export async function setSuperAiBusinessContext ({ type = 'Subscription', id, subId, data }) {
    return http
        .post(`/api/super/s/${subId}/businessContext`, {
            type,
            parentId: id,
            formData: {
                body: data,
            },
        })
        .then(({ data }) => data)
        .catch((error) => {
            throw error;
        });
}

export async function updateSuperAiBusinessContext ({ type = 'Subscription', id, subId, data }) {
    return http
        .patch(`/api/super/s/${subId}/businessContext/${type}/${id}`, {
            formData: {
                body: data,
            },
        })
        .then(({ data }) => data)
        .catch((error) => {
            throw error;
        });
}

export function startFreeTrial ({ subscriptionId, duration = 14, checklist, upfront }) {
    const params = new URLSearchParams({
        action: 'start',
        duration,
    });
    if (checklist) params.set('checklist', checklist);
    if (upfront) params.set('upfront', upfront);

    return http
        .post(`/api/super/s/${subscriptionId}/subscription/trial?${params}`, {
            headers: { 'Content-Type': 'application/json' },
        })
        .then((res) => res.data);
}

const dbPromise = openDB('super-store', 1, {
    upgrade (db) {
        if (!db.objectStoreNames.contains('subscriptions')) {
            db.createObjectStore('subscriptions', {
                keyPath: 'id',
                autoIncrement: true,
            });
        }
    },
});

export function minimizeSubForTableAndDb (sub) {
    const {
        id,
        name,
        displayName,
        organizationId,
        featureFlags,
        timeZone,
        eventCount,
        webCount,
        markedForDeletionAt,
        productLines,
    } = sub;

    const { blockSuperDataAccess, disabled, pendoFree } = featureFlags;
    const productLineMap = convertProductLinesArrayToMap(productLines);

    const tableFilterHelpers = {
        noEvents: eventCount <= 0,
        disabled,
        pendoFree,
        isAdoptPartner: hasPurchasedProductLine(productLineMap, 'adoptReseller'),
        isAdoptEndUser: hasPurchasedProductLine(productLineMap, 'adoptEndUser'),
        usesAdoptUI: hasPurchasedProductLine(productLineMap, 'adoptEndUser') &&
            productLineMap.adoptEndUser?.metadata?.userInterface === 'adopt',
        usesEngageUI: hasPurchasedProductLine(productLineMap, 'adoptEndUser') &&
            productLineMap.adoptEndUser?.metadata?.userInterface === 'engage',
        hasSessionReplay: hasPurchasedProductLine(productLineMap, 'sessionReplay'),
        extensionAppsEnabled: isExtensionEnabledForSubscription(sub),
        hasListen: hasPurchasedProductLine(productLineMap, 'voc'),
        hasListenExplore: hasPurchasedProductLine(productLineMap, 'voc', 'exploreSettings.enabled'),
        hasListenFreemium: hasPurchasedProductLine(productLineMap, 'voc', 'freemium.enabled'),
    };

    return {
        id,
        displayName,
        name,
        timeZone,
        eventCount,
        webCount,
        organizationId,
        markedForDeletionAt,
        productLines,
        tableFilterHelpers,
        featureFlags: {
            blockSuperDataAccess,
            disabled: !!disabled,
            pendoFree,
        },
    };
}

export const SuperListCacheStore = {
    async get (key) {
        return (await dbPromise).get('subscriptions', key);
    },
    async set (val) {
        const db = await dbPromise;
        const tx = db.transaction('subscriptions', 'readwrite');

        tx.store.put(val);

        await tx.done;
    },
    async delete (key) {
        return (await dbPromise).delete('subscriptions', key);
    },
    async clear () {
        return (await dbPromise).clear('subscriptions');
    },
    async keys () {
        return (await dbPromise).getAllKeys('subscriptions');
    },
    async getAll () {
        return (await dbPromise).getAll('subscriptions');
    },
    async setAll (values) {
        const db = await dbPromise;
        const tx = db.transaction('subscriptions', 'readwrite');

        await Promise.all([...values.map((val) => tx.store.put(val)), tx.done]);
    },
    // Prevent long wait times to check the store if a readwrite operation is underway
    async storeLikelyBlocked () {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 1000);

        const onTimeout = new Promise((resolve) => {
            controller.signal.addEventListener('abort', () => resolve(), { once: true });
        });

        const blocked = await Promise.race([dbPromise.then(() => false), onTimeout.then(() => true)]);

        clearTimeout(timeoutId);

        return blocked;
    },
};
